import Vec2 from 'vec2';

import {
  ApplianceModel,
  ApplianceSource,
  ApplianceType,
  DrawingAppliance,
  DrawingShape,
} from '~/core/models';

/**
 * Gets the approximate outline of an appliance as a polygon of Vec2 points
 * Used for collision detection and auto-placement calculations
 */
export const getApproximateApplianceOutline = (
  appliance: DrawingAppliance
): Vec2[] => [
  new Vec2(appliance.centerPoint.x + 22, appliance.centerPoint.y + 22),
  new Vec2(appliance.centerPoint.x + 22, appliance.centerPoint.y - 22),
  new Vec2(appliance.centerPoint.x - 22, appliance.centerPoint.y - 22),
  new Vec2(appliance.centerPoint.x - 22, appliance.centerPoint.y + 22),
  new Vec2(appliance.centerPoint.x + 22, appliance.centerPoint.y + 22),
];

export const getCutoutsCountFromShape = (
  drawingShapes: DrawingShape[],
  cutoutId: number
) =>
  drawingShapes.reduce(
    (count: number, shape: DrawingShape) =>
      count +
      shape.appliances.filter((appliance: DrawingAppliance) => {
        const { productId, applianceType } = appliance.applianceModel;
        // Only count explicit cutout types (ELECTRICAL_CUTOUT, FAUCET_CUTOUT)
        // Exclude sink labor products and other non-cutout appliances
        const isExplicitCutout =
          applianceType === ApplianceType.ELECTRICAL_CUTOUT ||
          applianceType === ApplianceType.FAUCET_CUTOUT;

        return productId === cutoutId && isExplicitCutout;
      }).length,
    0
  );

interface isCooktopOrRangeProps {
  applianceModel?: ApplianceModel;
  applianceType?: string;
}

export const isCooktop = ({
  applianceModel,
  applianceType,
}: isCooktopOrRangeProps) => {
  const type = applianceModel?.type ?? applianceType ?? '';

  return type.toUpperCase() === ApplianceType.COOKTOP;
};

export const isRange = ({
  applianceModel,
  applianceType,
}: isCooktopOrRangeProps) => {
  const type = applianceModel?.type ?? applianceType ?? '';

  return type.toUpperCase() === ApplianceType.RANGE;
};

/**
 * Determines if an appliance is customer-supplied based on its productSource
 * @param appliance The appliance to check
 * @returns true if the appliance is customer-supplied
 */
export const isCustomerSuppliedAppliance = (
  appliance?: ApplianceModel
): boolean => appliance?.productSource === ApplianceSource.CUSTOMER;

/**
 * Determines if an appliance is browsed (not customer-supplied) based on its productSource
 * @param appliance The appliance to check
 * @returns true if the appliance is browsed (IN_STORE or PROGRAM)
 */
export const isBrowsedAppliance = (appliance: ApplianceModel): boolean =>
  appliance.productSource === ApplianceSource.IN_STORE ||
  appliance.productSource === ApplianceSource.PROGRAM;
